
import React, { useState, useEffect } from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';

interface SafeMotionProps extends HTMLMotionProps<any> {
  fallback?: React.ReactNode;
  children?: React.ReactNode;
  as?: keyof HTMLElementTagNameMap;
}

// Synchronously check if motion is available
const isMotionAvailable = (() => {
  if (typeof window === 'undefined') return false;
  try {
    return typeof window.requestAnimationFrame === 'function';
  } catch {
    return false;
  }
})();

// Type guards for safe property access
const isPlainObject = (val: any): val is Record<string, any> => {
  return val !== null && typeof val === 'object' && !Array.isArray(val);
};

const num = (val: any): val is number => {
  return typeof val === 'number' && !isNaN(val);
};

// Helper to safely convert motion values to plain objects
const toObj = (val: any): Record<string, any> | null => {
  if (isPlainObject(val)) return val;
  return null;
};

// Helper to safely get numeric values from objects
const getNum = (obj: Record<string, any> | null, key: string): number | null => {
  if (!obj || !(key in obj)) return null;
  const val = obj[key];
  return num(val) ? val : null;
};

const SafeMotion: React.FC<SafeMotionProps> = ({ 
  children = null, 
  fallback, 
  className = '', 
  style, 
  as = 'div', 
  animate,
  initial,
  transition,
  whileHover,
  whileTap,
  ...motionProps 
}) => {
  const [shouldFallback, setShouldFallback] = useState(!isMotionAvailable);

  useEffect(() => {
    if (!isMotionAvailable && !shouldFallback) {
      console.info('SafeMotion: Using CSS fallbacks for animations');
      setShouldFallback(true);
    }
  }, [shouldFallback]);

  // Generate CSS animation classes based on motion props with safe type guards
  const getCSSFallbackClasses = () => {
    let classes = className;
    
    const initialObj = toObj(initial);
    const animateObj = toObj(animate);
    
    if (initialObj && animateObj) {
      const initialOpacity = getNum(initialObj, 'opacity');
      const animateOpacity = getNum(animateObj, 'opacity');
      const initialY = getNum(initialObj, 'y');
      const animateY = getNum(animateObj, 'y');
      const initialScale = getNum(initialObj, 'scale');
      const animateScale = getNum(animateObj, 'scale');
      
      if (initialOpacity === 0 && animateOpacity === 1) {
        classes += ' animate-fade-in';
      }
      if (initialY !== null && animateY === 0) {
        classes += ' animate-fade-in';
      }
      if (initialScale !== null && animateScale === 1) {
        classes += ' animate-scale-in';
      }
    }
    
    if (toObj(whileHover)) {
      classes += ' hover:scale-105 transition-transform duration-200';
    }
    
    return classes;
  };

  if (shouldFallback || !isMotionAvailable) {
    const Element = as;
    const fallbackClasses = getCSSFallbackClasses();
    console.info('SafeMotion: CSS fallback for element:', as);
    return (
      <Element 
        className={fallbackClasses} 
        style={style as React.CSSProperties}
      >
        {children ?? fallback}
      </Element>
    );
  }

  try {
    const MotionElement = motion[as] as any;
    return (
      <MotionElement 
        className={className} 
        style={style} 
        animate={animate}
        initial={initial}
        transition={transition}
        whileHover={whileHover}
        whileTap={whileTap}
        {...motionProps}
      >
        {children}
      </MotionElement>
    );
  } catch (error) {
    console.warn('SafeMotion runtime error, using fallback:', error);
    const Element = as;
    const fallbackClasses = getCSSFallbackClasses();
    return (
      <Element 
        className={fallbackClasses} 
        style={style as React.CSSProperties}
      >
        {children ?? fallback}
      </Element>
    );
  }
};

export default SafeMotion;
